import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useSearchParams } from "react-router-dom";
import { Star, Cake, Users, CheckCircle, TrendingUp, Ticket } from "lucide-react";
import { API_URL } from "../config";

/**
 * Public backstage display for TVs/monitors.
 * Protected by a shareable token: /backstage-display?token=xxxxx
 * Shows privacy-safe data (first name + last initial, no emails/phones).
 * Polls every 5s (SSE would require auth cookies).
 */
export default function BackstagePublic() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [stats, setStats] = useState(null);
  const [err, setErr] = useState(null);
  const [now, setNow] = useState(new Date());
  const flashRef = useRef(null);
  const lastCountRef = useRef(0);

  useEffect(() => {
    if (!token) {
      setErr("Token em falta na URL.");
      return;
    }
    let cancelled = false;

    const load = async () => {
      try {
        const r = await axios.get(`${API_URL}/api/backstage-display/stats`, { params: { token } });
        if (cancelled) return;
        setStats(r.data);
        setErr(null);
        // Flash on new check-in
        const newCount = r.data?.totals?.total_checked_in || 0;
        if (lastCountRef.current && newCount > lastCountRef.current && flashRef.current) {
          flashRef.current.style.opacity = "1";
          setTimeout(() => { if (flashRef.current) flashRef.current.style.opacity = "0"; }, 1500);
        }
        lastCountRef.current = newCount;
      } catch (e) {
        if (cancelled) return;
        const status = e?.response?.status;
        // Only show fatal error for auth failure; keep last-good stats on transient errors (network blip, 5xx)
        if (status === 401 || status === 404) {
          setErr(status === 401 ? "Token inválido ou expirado." : "Configuração em falta.");
        } else if (!stats) {
          setErr("Erro ao carregar dados.");
        }
        // else: keep showing previous stats silently (TV-friendly)
      }
    };

    load();
    const pollTimer = setInterval(load, 5000);
    const clockTimer = setInterval(() => setNow(new Date()), 30000);
    return () => {
      cancelled = true;
      clearInterval(pollTimer);
      clearInterval(clockTimer);
    };
  }, [token]);

  if (err) {
    return (
      <div className="min-h-screen bg-[#09090b] text-white flex items-center justify-center p-6" data-testid="backstage-public-error">
        <div className="text-center max-w-md">
          <h1 className="text-2xl font-bold mb-2">Acesso negado</h1>
          <p className="text-zinc-500">{err}</p>
          <p className="text-zinc-600 text-xs mt-6">Peça ao administrador o link correcto do painel de backstage público.</p>
        </div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="min-h-screen bg-[#09090b] text-white flex items-center justify-center" data-testid="backstage-public-loading">
        <div className="flex items-center gap-3 text-zinc-400">
          <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
          A carregar…
        </div>
      </div>
    );
  }

  const { totals, sessions, vips_arrived, birthdays_today, recent_checkins, show_name, accent_color } = stats;
  const overallPercent = totals.total_paid > 0 ? Math.round(totals.total_checked_in * 100 / totals.total_paid) : 0;
  const accent = accent_color || "#22c55e";

  return (
    <div className="min-h-screen bg-[#09090b] text-white p-6" data-testid="backstage-public-panel">
      {/* Flash on new check-in */}
      <div
        ref={flashRef}
        style={{
          position: "fixed",
          inset: 0,
          pointerEvents: "none",
          background: `radial-gradient(circle, ${accent}33, transparent 70%)`,
          opacity: 0,
          transition: "opacity 0.8s",
          zIndex: 1,
        }}
      />

      <div className="max-w-[1800px] mx-auto relative">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl md:text-5xl font-black tracking-tight">{show_name || "Backstage"} · Tempo Real</h1>
            <p className="text-zinc-500 text-sm mt-1">
              Atualizado automaticamente · {now.toLocaleTimeString("pt-PT", { hour: "2-digit", minute: "2-digit" })}
            </p>
          </div>
          <div className="flex items-center gap-2 text-sm font-medium" style={{ color: accent }}>
            <span
              className="w-2.5 h-2.5 rounded-full animate-pulse"
              style={{ background: accent }}
            />
            AO VIVO
          </div>
        </div>

        {/* Totals */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Stat icon={<Ticket className="w-5 h-5" />} label="Bilhetes Vendidos" value={totals.total_paid} accent="text-zinc-300" />
          <Stat icon={<CheckCircle className="w-5 h-5" />} label="Check-ins" value={`${totals.total_checked_in} / ${totals.total_paid}`} sub={`${overallPercent}%`} accentStyle={{ color: accent }} />
          <Stat icon={<Star className="w-5 h-5" />} label="VIPs Chegaram" value={totals.total_vips_arrived} accent="text-amber-400" />
          <Stat icon={<Cake className="w-5 h-5" />} label="Aniversariantes Hoje" value={totals.total_birthdays_today} accent="text-pink-400" />
        </div>

        {/* Per-session occupancy bars */}
        <div className="bg-zinc-900 rounded-2xl p-6 border border-white/10 mb-6" data-testid="backstage-public-sessions">
          <h2 className="text-lg font-semibold mb-1 flex items-center gap-2">
            <TrendingUp className="w-5 h-5" style={{ color: accent }} /> Ocupação por Sessão
          </h2>
          <p className="text-zinc-500 text-sm mb-5">Check-ins efectivos · lotação total</p>
          <div className="space-y-4">
            {sessions.length === 0 && <div className="text-zinc-500 text-center py-4">Ainda não há sessões.</div>}
            {sessions.map((s) => {
              const percent = s.total_seats > 0 ? Math.min(100, s.checked_in * 100 / s.total_seats) : 0;
              const salesPercent = s.total_seats > 0 ? Math.min(100, s.paid * 100 / s.total_seats) : 0;
              return (
                <div key={s.id}>
                  <div className="flex items-end justify-between mb-2">
                    <div className="min-w-0 flex-1 pr-3">
                      <div className="flex items-center gap-2 flex-wrap">
                        {s.room_name && (
                          <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-violet-500/15 text-violet-300 border border-violet-500/30">
                            {s.room_name}
                          </span>
                        )}
                        <span className="font-semibold text-white text-lg">{s.name || "(sem nome)"}</span>
                      </div>
                      {s.subtitle && (
                        <div className="text-sm text-zinc-400 truncate mt-0.5">{s.subtitle}</div>
                      )}
                      <div className="text-xs text-zinc-500 mt-1">{s.date}{s.time ? ` · ${s.time}` : ""}</div>
                      <div className="text-xs text-zinc-500 mt-1">
                        <span className="font-medium" style={{ color: accent }}>{s.checked_in} check-ins</span>
                        <span className="mx-1">/</span>
                        <span>{s.paid} vendidos</span>
                        <span className="mx-1">·</span>
                        <span>{s.total_seats} lugares</span>
                        {s.vip_checked_in > 0 && <span className="ml-2 text-amber-400">⭐ {s.vip_checked_in} VIP</span>}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-bold text-2xl" style={{ color: accent }}>{Math.round(percent)}%</div>
                    </div>
                  </div>
                  <div className="relative h-4 bg-zinc-800 rounded-full overflow-hidden">
                    <div className="absolute inset-y-0 left-0 bg-blue-500/30 rounded-full" style={{ width: `${salesPercent}%` }} />
                    <div className="absolute inset-y-0 left-0 rounded-full transition-all" style={{ width: `${percent}%`, background: accent }} />
                  </div>
                </div>
              );
            })}
            <div className="pt-2 flex items-center gap-4 text-xs text-zinc-500">
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded" style={{ background: accent }} /> Check-in feito</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-blue-500/30" /> Vendido (por chegar)</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-zinc-800" /> Disponível</span>
            </div>
          </div>
        </div>

        {/* Two columns: Recent + VIPs/Birthdays */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="bg-zinc-900 rounded-2xl p-6 border border-white/10 lg:col-span-2" data-testid="backstage-public-recent">
            <h2 className="text-lg font-semibold mb-1">Check-ins Recentes</h2>
            <p className="text-zinc-500 text-sm mb-4">Últimos 20</p>
            {recent_checkins.length === 0 ? (
              <div className="text-zinc-500 text-center py-8">Ainda ninguém fez check-in.</div>
            ) : (
              <div className="space-y-2 max-h-[500px] overflow-y-auto pr-2">
                {recent_checkins.map((r, i) => (
                  <div key={i} className="flex items-center justify-between p-3 bg-zinc-950 rounded-lg">
                    <div className="flex items-center gap-3 min-w-0">
                      <CheckCircle className="w-4 h-4 shrink-0" style={{ color: accent }} />
                      <div className="min-w-0">
                        <div className="font-medium truncate">
                          {r.first_name} {r.last_initial}
                          {r.is_vip && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-300">⭐ VIP</span>}
                          {r.group_code && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-indigo-500/15 text-indigo-300 font-mono">{r.group_code}</span>}
                        </div>
                      </div>
                    </div>
                    <div className="text-xs text-zinc-500 shrink-0 ml-2">{formatTime(r.checked_in_at)}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="space-y-6">
            <div className="bg-gradient-to-br from-amber-500/10 to-amber-500/5 border border-amber-500/30 rounded-2xl p-6" data-testid="backstage-public-vips">
              <h2 className="text-lg font-semibold mb-1 flex items-center gap-2"><Star className="w-5 h-5 text-amber-400" /> VIPs Chegaram</h2>
              {vips_arrived.length === 0 ? (
                <div className="text-zinc-500 text-sm py-3">Nenhum VIP ainda.</div>
              ) : (
                <div className="space-y-2 mt-3">
                  {vips_arrived.map((v, i) => (
                    <div key={i} className="flex items-center justify-between p-2 bg-amber-500/5 rounded">
                      <span className="text-sm">{v.first_name} {v.last_initial}</span>
                      <span className="text-xs text-zinc-500">{formatTime(v.checked_in_at)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="bg-gradient-to-br from-pink-500/10 to-pink-500/5 border border-pink-500/30 rounded-2xl p-6" data-testid="backstage-public-birthdays">
              <h2 className="text-lg font-semibold mb-1 flex items-center gap-2"><Cake className="w-5 h-5 text-pink-400" /> Aniversariantes Hoje</h2>
              {birthdays_today.length === 0 ? (
                <div className="text-zinc-500 text-sm py-3">Nenhum aniversariante fez check-in hoje.</div>
              ) : (
                <div className="space-y-2 mt-3">
                  {birthdays_today.map((b, i) => (
                    <div key={i} className="flex items-center justify-between p-2 bg-pink-500/5 rounded">
                      <span className="text-sm">🎂 {b.first_name} {b.last_initial}</span>
                      <span className="text-xs text-zinc-500">{formatTime(b.checked_in_at)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Stat({ icon, label, value, sub, accent, accentStyle }) {
  return (
    <div className="bg-zinc-900 rounded-2xl p-5 border border-white/10">
      <div className={`flex items-center gap-2 text-xs uppercase tracking-wider mb-2 ${accent || "text-zinc-400"}`} style={accentStyle}>
        {icon} {label}
      </div>
      <div className="text-3xl md:text-4xl font-black">{value}</div>
      {sub && <div className={`text-sm mt-1 ${accent || "text-zinc-500"}`} style={accentStyle}>{sub}</div>}
    </div>
  );
}

function formatTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString("pt-PT", { hour: "2-digit", minute: "2-digit" });
  } catch (_) { return ""; }
}
